import { NgClass } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

import { getPropsFrom, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import { MatIcon } from '@angular/material/icon';
import { EditorDialogService } from '../../../shared/services/editor-dialog.service';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { extractColorProperties, getColors, getTextColorsPropertiesStyle } from '../../../../utils/colors';
import { hasValidGroupPermission } from '../../../../utils/hasValidGroupPermission';

interface AuthenticationItemProperties {
  text: string;
  style: string;
  label: string;
  groupItemIds: string[];
  groupItems: string[];
  signed: string;
  userRole: string;
}

@Component({
  selector: 'lfe-authentication-item-form',
  imports: [FormsModule, ReactiveFormsModule, MatIcon, NgClass],
  templateUrl: './authentication-item-form.component.html',
  standalone: true,
  styleUrl: './authentication-item-form.component.css'
})
export class AuthenticationItemFormComponent
  extends InteractiveItemComponent<AuthenticationItemProperties>
  implements OnInit, OnDestroy
{
  private readonly editorDialogService = inject(EditorDialogService);
  private readonly translateService = inject(LiveFormsTranslateLoaderService);
  private valueChangesSubscription?: Subscription;
  formControl: FormControl<boolean | null> | undefined;
  properties: InteractiveItemProperties & AuthenticationItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    groupItems: [],
    groupItemIds: [],
    text: this.translateService.instant('liveforms-editor.authentication-item.default-text'),
    style: 'Normal',
    label: this.translateService.instant('liveforms-editor.authentication-item.label'),
    signed: 'False',
    userRole: '' // TODO: what is the actual property for getting the roles?
  };

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `authentication_${this.currentId()}`);

  currentTextColor = signal('');
  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));

  resultUserSignal = signal('');
  resultTimeSignal = signal<Date>(new Date());
  formValueSignal = signal(false);

  signatureInfo = computed(() =>
    this.translateService.instant('liveforms-editor.authentication-item.signature-info', {
      user: this.resultUserSignal(),
      date: this.resultTimeSignal().toLocaleDateString('de', { day: '2-digit', month: '2-digit', year: 'numeric' })
    })
  );

  resultValue = computed(() =>
    this.translateService.instant('liveforms-editor.authentication-item.result-value', {
      user: this.resultUserSignal(),
      date: this.resultTimeSignal().toLocaleDateString('de'),
      time: this.resultTimeSignal().toLocaleTimeString('de')
    })
  );

  canSign = computed(() =>
    hasValidGroupPermission({
      properties: this.properties,
      defaultGroupName: this.translateService.instant('liveforms-editor.common.default-group-name'),
      userGroups: this.user().userGroups
    })
  );

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = !!this.properties.resultValue;
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    // Initialize signals with current property values
    this.formValueSignal.set(initialValue);
    this.resultUserSignal.set(this.properties.resultUser);
    this.resultTimeSignal.set(this.properties.resultTime ? new Date(this.properties.resultTime) : new Date());

    // Subscribe to form control changes and update the signal
    this.valueChangesSubscription = this.formControl.valueChanges.subscribe((value) => {
      this.formValueSignal.set(value || false);
    });
  }

  override ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  override getXML(): string {
    const authenticated = this.properties.resultValue ? 'True' : 'False';

    const element = this.getXmlCloneElement();

    upsertChildren(element, {
      Authenticated: authenticated
    });

    if (this.properties.resultValue) {
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & AuthenticationItemProperties {
    const props = getPropsFrom(xml);

    const sGroupItemIds = props['GroupItemIDs'];
    const sGroupItems = props['GroupItems'];

    const groupItemIds: string[] = sGroupItemIds ? sGroupItemIds.split(';').map((id) => id.trim()) : [];
    const groupItems: string[] = sGroupItems
      ? sGroupItems.split(';').map((name) => name.trim())
      : [this.translateService.instant('liveforms-editor.common.default-group-name')];

    const customId = props['CustomID'] || '';
    const id = props['ID'] || '';
    const text = props['Text'] || this.translateService.instant('liveforms-editor.authentication-item.default-text');
    const { textBgColor, textColor } = getColors(extractColorProperties(props));
    this.currentTextColor.set(textColor);
    const label = props['Label'] || this.translateService.instant('liveforms-editor.authentication-item.label');
    const signed = props['Signed'] || 'False';
    const style = props['Style'] || 'Normal';
    const userRole = props['UserRole'] || '';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      groupItemIds,
      groupItems,
      resultValue,
      resultUser,
      resultTime,
      text,
      style,
      textBgColor,
      label,
      signed,
      userRole,
      isGenericRepeatItem
    };
  }

  onSignatureClick(): void {
    const now = new Date();

    // Update user signal (form value signal will be updated by valueChanges subscription)
    this.resultUserSignal.set(this.user().displayName);
    this.resultTimeSignal.set(now);

    this.properties.signed = 'True';

    this.formControl?.setValue(true);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = now.toISOString();
    this.properties.resultValue = this.resultValue();
  }

  async resetSignature(): Promise<void> {
    const result = await this.editorDialogService.showUserPrompt({
      title: this.translateService.instant('liveforms-editor.authentication-item.dialog.reset.title'),
      message: this.translateService.instant('liveforms-editor.authentication-item.dialog.reset.message'),
      icon: 'info',
      iconColor: '#1976d2',
      actions: [
        {
          text: this.translateService.instant('liveforms-editor.authentication-item.dialog.reset.actions.yes'),
          action: 'confirm',
          color: 'primary',
          variant: 'raised'
        },
        {
          text: this.translateService.instant('liveforms-editor.authentication-item.dialog.reset.actions.no'),
          action: 'cancel',
          color: 'primary',
          variant: 'basic'
        },
        {
          text: this.translateService.instant('liveforms-editor.authentication-item.dialog.reset.actions.cancel'),
          action: 'abort',
          color: 'primary',
          variant: 'basic'
        }
      ]
    });

    if (result === 'confirm') {
      this.properties.signed = 'False';

      this.formControl?.setValue(false);
      this.formControl?.markAsDirty();
      this.formControl?.markAsTouched();

      this.properties.resultValue = '';
      this.properties.resultUser = '';
      this.properties.resultTime = '';

      // Update user signal (form value signal will be updated by valueChanges subscription)
      this.resultUserSignal.set('');
      this.resultTimeSignal.set(new Date());
    }
  }
}
